import React from 'react';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import { scoreToColor } from './DashboardTeam.utils';

interface DoughnutGaugeProps {
  title?: string;
  max?: number;
  complete: number;
  percent?: boolean;
  precision?: number;
  size?: 'tiny' | 'small' | 'smallPlus' | 'medium' | 'large';
  colorize?: boolean;
  color?: string;
  noWrap?: boolean; // New property for controlling white-space
}

const DoughnutGauge: React.FC<DoughnutGaugeProps> = ({
  title = '',
  max = 100,
  complete,
  percent = false,
  precision = 0,
  size = 'medium',
  colorize = false,
  color = '',
  noWrap = false,
}) => {
  // Calculate the completion percentage
  const completionPercentage = (complete / max) * 100;

  // Determine the color based on the colorize prop
  const getCompletionColor = () => {
    if (color) {
      if (color === 'red') {
        return 'rgba(255, 99, 132, 0.6)'; // Red
      } else if (color === 'yellow') {
        return 'rgba(255, 216, 111, 1)'; // Yellow
      } else if (color === 'green') {
        return 'rgba(129, 199, 132, 1)'; // Green
      }
    }

    if (colorize) {
      const normalizedScore = Math.max(0, Math.min(10, (completionPercentage / 100) * 10));
      return scoreToColor(normalizedScore);
    }
    return '#a5cfee'; // Default color (blueish)
  };

  const doughnutData = {
    labels: ['Completion', 'Remaining'],
    datasets: [
      {
        data: [complete, max - complete],
        backgroundColor: [getCompletionColor(), '#ddd'], // Colorize the "Completion" segment
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
    },
    cutout: '60%',
    responsive: true,
  };

  return (
    <DoughnutContainer size={size}>
      <Doughnut data={doughnutData} options={doughnutOptions} />
      <DoughnutLabel size={size} noWrap={noWrap}>
        {title}
      </DoughnutLabel>
      <Munchkin size={size} percent={percent}>
        {complete.toFixed(precision)}
        {percent ? '%' : ''}
      </Munchkin>
    </DoughnutContainer>
  );
};

export default DoughnutGauge;

const DoughnutContainer = styled.div<{ size: 'tiny' | 'small' | 'smallPlus' | 'medium' | 'large' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: ${props =>
    props.size === 'tiny'
      ? '50px'
      : props.size === 'small'
        ? '70px'
        : props.size === 'smallPlus'
          ? '84px'
          : props.size === 'large'
            ? '150px'
            : '110px'};
  height: ${props =>
    props.size === 'tiny'
      ? '50px'
      : props.size === 'small'
        ? '70px'
        : props.size === 'smallPlus'
          ? '84px'
          : props.size === 'large'
            ? '150px'
            : '110px'};
`;

const DoughnutLabel = styled.div<{ size: 'tiny' | 'small' | 'smallPlus' | 'medium' | 'large'; noWrap: boolean }>`
  font-family: 'Poppins', sans-serif;
  font-size: ${props =>
    props.size === 'tiny'
      ? '11px'
      : props.size === 'small'
        ? '12px'
        : props.size === 'smallPlus'
          ? '13px'
          : props.size === 'large'
            ? '28px'
            : '14px'};
  color: #555;
  font-weight: 500;
  text-align: center;
  margin-top: 5px;
  white-space: ${props =>
    props.noWrap ? 'nowrap' : 'normal'}; // Apply white-space based on noWrap prop
`;

const Munchkin = styled.div<{ size: 'tiny' | 'small' | 'smallPlus' | 'medium' | 'large'; percent: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Poppins', sans-serif;
  font-size: ${props =>
    props.size === 'tiny' && props.percent
      ? '11px'
      : props.size === 'tiny' && !props.percent
        ? '12px'
        : props.size === 'small'
          ? '14px'
          : props.size === 'smallPlus'
            ? '16px'
            : props.size === 'large'
              ? '28px'
              : '20px'};
  font-weight: ${props =>
    props.size === 'tiny'
      ? '600'
      : props.size === 'small'
        ? '700'
        : props.size === 'large'
          ? '700'
          : '700'};
  color: #333;
`;
